/**
 * Shared helpers for loading a firm calculation template into a target
 * calculation (offer page or standalone /dashboard/offers/calculations editor).
 *
 * `fetchOfferCalculation` returns the template's own item ids. When copying
 * into another calculation those ids belong elsewhere, so create/update would
 * silently no-op — strip them so every line is created fresh.
 *
 * Product lock-in: importing a linjekalkyle into an offer copies lines/nested
 * calcs only. Offer::Group structure is never auto-created — the user owns
 * chapters explicitly (add structure / move lines).
 */

import {
  flattenGroups,
  suggestNextTopLevelNumber,
} from "./offer-group-hierarchy";

export const stripItemIdsForCopy = (items, { keepOfferGroupId = false } = {}) =>
  (items || []).map(
    ({
      id,
      offer_group_id,
      offerGroupId,
      nested_calculation_id,
      nested_calculation,
      ...rest
    }) => ({
      ...rest,
      offer_group_id: keepOfferGroupId
        ? offer_group_id ?? offerGroupId ?? null
        : null,
      // Never keep firm-template nested ids — that redirects Edit calc to /calculations/:malId.
      nested_calculation_id: null,
      nested_calculation: undefined,
    })
  );

/** Stamp offer_group_id on live (non-delete) rows. */
export function assignItemsToOfferGroup(items, offerGroupId) {
  const gid = offerGroupId == null || offerGroupId === "" ? null : offerGroupId;
  return (items || []).map((row) => {
    if (!row || row.delete) return row;
    return { ...row, offer_group_id: gid };
  });
}

/** Recursively stamp offer_group_id on nested calculation item trees. */
export function assignNestedCalculationsToOfferGroup(
  nestedCalculations,
  offerGroupId
) {
  return (nestedCalculations || []).map((nested) => ({
    ...nested,
    items: assignItemsToOfferGroup(nested.items, offerGroupId),
    nested_calculations: assignNestedCalculationsToOfferGroup(
      nested.nested_calculations,
      offerGroupId
    ),
  }));
}

/**
 * Build the groups[] payload that appends one top-level Offer::Group for a
 * template import (pure — caller persists via updateOfferAsync).
 */
export function buildTemplateImportGroupPayload(existingGroups, title) {
  const trimmed = String(title || "").trim() || "Struktur";
  const existing = flattenGroups(existingGroups || []);
  const number = suggestNextTopLevelNumber(existing);
  return {
    number,
    title: trimmed,
    groups: [
      ...existing.map((g, index) => ({
        id: g.id,
        title: g.title || "",
        comment: g.comment || g.description || "",
        row_order: g.row_order ?? index,
        parent_id: g.parent_id ?? null,
        number: g.number || null,
        discipline: g.discipline || null,
        group_type: g.group_type || null,
      })),
      {
        title: trimmed,
        comment: "",
        row_order: existing.length,
        parent_id: null,
        number,
        discipline: trimmed,
        group_type: "section",
      },
    ],
  };
}

/** Find the group created by buildTemplateImportGroupPayload after refresh. */
export function findTemplateImportGroup(groups, { number, title }) {
  const flat = flattenGroups(groups || []);
  const trimmed = String(title || "").trim();
  return (
    flat.find(
      (g) =>
        String(g.number || "") === String(number) &&
        String(g.title || "").trim() === trimmed
    ) ||
    flat
      .filter((g) => String(g.title || "").trim() === trimmed)
      .sort((a, b) => Number(b.id) - Number(a.id))[0] ||
    null
  );
}

/** Strip nested OfferCalculation ids recursively so copies never reuse template rows. */
export const stripNestedCalculationIdsForCopy = (
  nestedCalculations,
  { keepOfferGroupId = false } = {}
) =>
  (nestedCalculations || []).map(({ id, nested_calculations, items, ...rest }) => ({
    ...rest,
    items: stripItemIdsForCopy(items, { keepOfferGroupId }),
    nested_calculations: stripNestedCalculationIdsForCopy(nested_calculations, {
      keepOfferGroupId,
    }),
  }));

/**
 * Ensure every nested child under a page calc also has a package-shell item
 * on `items`. Template import often upserts nested_calculations only — Kalkyle
 * UI then shows «no lines» while Customer view still walks nested.
 */
export function ensureNestedLinkedAsItems(calc) {
  if (!calc) return calc;
  const nested = Array.isArray(calc.nested_calculations)
    ? calc.nested_calculations
    : [];
  if (!nested.length) return calc;

  const liveItems = [...(calc.items || [])].filter((row) => !row?.delete);
  const deletedItems = [...(calc.items || [])].filter((row) => row?.delete);
  const linked = new Set(
    liveItems
      .map((row) => row?.nested_calculation_id)
      .filter((id) => id != null)
      .map(String)
  );

  const extras = [];
  nested.forEach((child, index) => {
    if (!child) return;
    const childId = child.id;
    if (childId != null && linked.has(String(childId))) return;
    const key =
      childId != null ? String(childId) : `pending:${child.title || index}`;
    if (linked.has(key)) return;
    linked.add(key);
    extras.push({
      description: child.title || "",
      purchase_price: child.purchase_amount ?? child.cost_total ?? 0,
      sales_price: child.total_amount ?? 0,
      quantity: 1,
      discount: 0,
      surcharge: 0,
      row_order: liveItems.length + extras.length,
      line_type: "calculation",
      item_type: "product",
      nested_calculation_id: childId ?? null,
      nested_calculation: child,
      offer_group_id: null,
      draggableId: `client-nested-shell-${Date.now()}-${index}`,
      delete: false,
    });
  });

  if (!extras.length) return calc;
  return {
    ...calc,
    items: [...deletedItems, ...liveItems, ...extras],
  };
}

export function resolveCalculationTypeKey(calcData, fallback = "standard") {
  if (typeof calcData?.calculation_type === "string") {
    return calcData.calculation_type;
  }
  return calcData?.calculation_type?.key || fallback;
}

export function resolveCalculationTypeId(calcData, fallback = null) {
  return (
    calcData?.calculation_type_id ||
    (typeof calcData?.calculation_type === "object"
      ? calcData.calculation_type?.id
      : null) ||
    fallback
  );
}

/** Workspace defaults that should yield to the template title on replace. */
const GENERIC_CALCULATION_TITLES = new Set([
  "ny kalkyle",
  "new calculation",
  "ny kalkyl",
]);

/**
 * True when the current title is blank or a generic empty-workspace default.
 * Replace should then take the template title; otherwise preserve the user's title.
 */
export function isBlankOrGenericCalculationTitle(title) {
  const trimmed = String(title ?? "").trim();
  if (!trimmed) return true;
  return GENERIC_CALCULATION_TITLES.has(trimmed.toLowerCase());
}

/**
 * On template replace: keep a meaningful current title; apply template title
 * only when the current title is blank/missing/generic (or create-from-template
 * with an empty parent title).
 */
export function resolveTitleOnTemplateReplace(currentTitle, templateTitle) {
  if (isBlankOrGenericCalculationTitle(currentTitle)) {
    return templateTitle || currentTitle || "";
  }
  return currentTitle;
}

/** Remap offer_group_id on items using { oldId: newId } map (string keys OK). */
export function remapItemOfferGroupIds(items, idMap) {
  if (!idMap || !Object.keys(idMap).length) {
    return items || [];
  }
  return (items || []).map((row) => {
    if (!row || row.delete) return row;
    const oldId = row.offer_group_id ?? row.offerGroupId;
    if (oldId == null || oldId === "") return { ...row, offer_group_id: null };
    const mapped = idMap[String(oldId)] ?? idMap[oldId];
    return {
      ...row,
      offer_group_id: mapped != null ? mapped : null,
    };
  });
}

/** Recursively remap offer_group_id on nested calculation trees. */
export function remapNestedOfferGroupIds(nestedCalculations, idMap) {
  return (nestedCalculations || []).map((nested) => ({
    ...nested,
    items: remapItemOfferGroupIds(nested.items, idMap),
    nested_calculations: remapNestedOfferGroupIds(
      nested.nested_calculations,
      idMap
    ),
  }));
}

/**
 * Build Formik/API values that replace the target calculation's content with
 * the expanded template (marks existing item rows for delete).
 * When `offerGroupId` is set, every live imported line is placed in that group.
 * When `groupIdMap` is set, template offer_group_ids are remapped (preserves
 * multi-chapter structure from the mal).
 */
export function buildReplaceFromTemplateValues(
  parent,
  expanded,
  { offerGroupId = null, groupIdMap = null } = {}
) {
  const deletedExisting = (parent.items || [])
    .filter((row) => row?.id)
    .map((row) => ({ ...row, delete: true }));

  const keepGroups = Boolean(groupIdMap && Object.keys(groupIdMap).length);
  let importedItems = stripItemIdsForCopy(expanded.items, {
    keepOfferGroupId: keepGroups,
  });
  let importedNested = stripNestedCalculationIdsForCopy(
    expanded.nested_calculations,
    { keepOfferGroupId: keepGroups }
  );

  if (keepGroups) {
    importedItems = remapItemOfferGroupIds(importedItems, groupIdMap);
    importedNested = remapNestedOfferGroupIds(importedNested, groupIdMap);
  } else if (offerGroupId != null) {
    importedItems = assignItemsToOfferGroup(importedItems, offerGroupId);
    importedNested = assignNestedCalculationsToOfferGroup(
      importedNested,
      offerGroupId
    );
  }

  const next = {
    ...parent,
    original_calculation_id: expanded.id,
    title: resolveTitleOnTemplateReplace(parent.title, expanded.title),
    description: expanded.description || parent.description,
    calculation_type: resolveCalculationTypeKey(
      expanded,
      parent.calculation_type
    ),
    calculation_type_id: resolveCalculationTypeId(
      expanded,
      parent.calculation_type_id
    ),
    show_hours: expanded.show_hours,
    show_quantities: expanded.show_quantities,
    show_products: expanded.show_products,
    show_setup_only: expanded.show_setup_only,
    show_quantities_for_offer_item_only:
      expanded.show_quantities_for_offer_item_only,
    include_product_short_description:
      expanded.include_product_short_description,
    module_enabled: Boolean(expanded.module_enabled),
    module_graph: expanded.module_graph,
    module_result: expanded.module_result,
    items: [...deletedExisting, ...importedItems],
    nested_calculations: importedNested,
  };
  const linked = ensureNestedLinkedAsItems(next);
  if (keepGroups) {
    return {
      ...linked,
      items: remapItemOfferGroupIds(linked.items, groupIdMap),
      nested_calculations: remapNestedOfferGroupIds(
        linked.nested_calculations,
        groupIdMap
      ),
    };
  }
  if (offerGroupId == null) return linked;
  return {
    ...linked,
    items: assignItemsToOfferGroup(linked.items, offerGroupId),
    nested_calculations: assignNestedCalculationsToOfferGroup(
      linked.nested_calculations,
      offerGroupId
    ),
  };
}

/** Payload to POST a nested copy of a template under a parent calculation. */
export function buildNestedCopyFromTemplatePayload(
  expanded,
  { parentId, offerId = null, offerGroupId = null, groupIdMap = null }
) {
  const keepGroups = Boolean(groupIdMap && Object.keys(groupIdMap).length);
  let items = stripItemIdsForCopy(expanded.items, {
    keepOfferGroupId: keepGroups,
  });
  let nested = stripNestedCalculationIdsForCopy(expanded.nested_calculations, {
    keepOfferGroupId: keepGroups,
  });

  if (keepGroups) {
    items = remapItemOfferGroupIds(items, groupIdMap);
    nested = remapNestedOfferGroupIds(nested, groupIdMap);
  } else if (offerGroupId != null) {
    items = assignItemsToOfferGroup(items, offerGroupId);
    nested = assignNestedCalculationsToOfferGroup(nested, offerGroupId);
  }

  return {
    offer_id: offerId,
    parent_calculation_id: parentId,
    original_calculation_id: expanded.id,
    title: expanded.title,
    description: expanded.description,
    calculation_type: resolveCalculationTypeKey(expanded),
    calculation_type_id: resolveCalculationTypeId(expanded),
    active: true,
    show_hours: expanded.show_hours,
    show_quantities: expanded.show_quantities,
    show_products: expanded.show_products,
    show_setup_only: expanded.show_setup_only,
    show_quantities_for_offer_item_only:
      expanded.show_quantities_for_offer_item_only,
    include_product_short_description:
      expanded.include_product_short_description,
    module_enabled: expanded.module_enabled,
    module_graph: expanded.module_graph,
    module_result: expanded.module_result,
    items,
    nested_calculations: nested,
  };
}

/**
 * Payload to POST a blank nested calculation (kalkyle i kalkyle) under a parent.
 * Prefer this for new reusable sub-scopes; use Offer::Group for presentation
 * chapters within one calculation (ADR-005).
 */
export function buildBlankNestedCalculationPayload({
  parentId,
  offerId = null,
  title = "Ny nestet kalkyle",
}) {
  return {
    offer_id: offerId,
    parent_calculation_id: parentId,
    original_calculation_id: null,
    title,
    description: "",
    calculation_type: "standard",
    calculation_type_id: null,
    active: true,
    show_hours: true,
    show_quantities: true,
    show_products: true,
    show_setup_only: false,
    show_quantities_for_offer_item_only: false,
    include_product_short_description: false,
    module_enabled: false,
    module_graph: null,
    module_result: null,
    items: [],
    nested_calculations: [],
  };
}

export function nestedLineFromCopy(
  savedCopy,
  rowOrder,
  existingRow = null,
  { offerGroupId = undefined } = {}
) {
  const resolvedGroupId =
    offerGroupId !== undefined
      ? offerGroupId
      : existingRow?.offer_group_id ?? existingRow?.offerGroupId ?? null;
  return {
    ...(existingRow || {}),
    description: savedCopy.title || existingRow?.description || "",
    purchase_price: savedCopy.purchase_amount ?? savedCopy.cost_total ?? 0,
    sales_price: savedCopy.total_amount ?? 0,
    quantity: existingRow?.quantity || 1,
    discount: existingRow?.discount || 0,
    surcharge: existingRow?.surcharge || 0,
    row_order: rowOrder,
    line_type: "calculation",
    price_mode: "custom",
    nested_calculation_id: savedCopy.id,
    nested_calculation: savedCopy,
    offer_group_id: resolvedGroupId,
    draggableId:
      existingRow?.draggableId ||
      `client-nested-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    delete: false,
  };
}

export function calculationHasReplaceableContent(parent) {
  return Boolean(
    (parent?.items || []).some((row) => !row.delete) ||
      (parent?.nested_calculations || []).length > 0 ||
      parent?.module_enabled ||
      parent?.module_graph
  );
}
